/**
 * Protocol Workspace Page
 *
 * 3-column layout for browsing protocol structure:
 * - Left: Protocol structure tree (forms, variables, conditions, etc.)
 * - Center: Content viewer (element details when selected)
 * - Right: Session requirements panel
 *
 * Uses modular-ui components: PageLayout, MainInfoBar, DualResizableContainer,
 * ProgressOverlay, ProgressIndicator.
 *
 * Session-based: Uses session ID to load workspace data.
 */

import React, {useState, useEffect, useCallback} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as FormIcon,
  Settings as SettingsIcon,
  Functions as VariableIcon,
  Code as CodeListIcon,
  Event as EventIcon,
  Rule as ConditionIcon,
  Assessment as EvaluationIcon,
  PlayArrow as ActionIcon,
  Folder as FolderIcon,
  Article as ContentIcon,
  CheckCircle as DoneIcon,
  Pending as PendingIcon,
  Schedule as PlannedIcon,
} from '@mui/icons-material';
import {
  PageLayout,
  DualResizableContainer,
  ProgressOverlay,
  ProgressIndicator,
} from '@signanthealth/modular-ui';
import {extractorService} from '../services/extractor';
import {apiService} from '../services/api';
import {ProtocolStructure} from '../types';

// ============================================
// TYPES
// ============================================

interface TreeNode {
  id: string;
  name: string;
  type: string;
  count?: number;
  children?: TreeNode[];
  formId?: string;
  data?: any;
}

interface SelectedElement {
  id: string;
  type: string;
  name?: string;
  formId?: string;
  data?: any;
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================

const ProtocolWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const {trialId, sessionId, platform} = useParams<{
    trialId: string;
    sessionId: string;
    platform: 'app' | 'web';
  }>();

  // Structure state
  const [, setStructure] = useState<ProtocolStructure | null>(null);
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [selectedElement, setSelectedElement] = useState<SelectedElement | null>(null);

  // Element list (for selected category)
  const [elementList, setElementList] = useState<any[]>([]);

  // Element detail (for selected element)
  const [elementDetail, setElementDetail] = useState<any>(null);
  const [elementDetailLoading, setElementDetailLoading] = useState(false);

  // Requirements state
  const [requirements, setRequirements] = useState<any>(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Session metadata
  const [sessionName, setSessionName] = useState<string>('');

  // ============================================
  // DATA LOADING
  // ============================================

  useEffect(() => {
    const loadWorkspace = async () => {
      if (!trialId || !sessionId || !platform) return;

      try {
        setLoading(true);
        setError(null);

        // Load in parallel: structure, requirements, session metadata
        const [structureData, reqsData] = await Promise.all([
          extractorService.getProtocolStructure(trialId, sessionId, platform as 'app' | 'web').catch((err) => {
            console.log('[Workspace] Could not load protocol structure:', err);
            return null;
          }),
          apiService.getSessionRequirements(trialId, sessionId, platform as 'app' | 'web').catch(() => null),
        ]);

        // Load session metadata
        try {
          const metadata = await apiService.getSessionMetadata(trialId, sessionId);
          if (metadata) {
            setSessionName(metadata.name || sessionId);
          }
        } catch {
          setSessionName(sessionId);
        }

        if (structureData) {
          setStructure(structureData);
          setTreeNodes(buildTreeNodes(structureData));
        }

        if (reqsData) {
          setRequirements(reqsData);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load workspace');
      } finally {
        setLoading(false);
      }
    };

    loadWorkspace();
  }, [trialId, sessionId, platform]);

  // ============================================
  // TREE BUILDING
  // ============================================

  const buildTreeNodes = (struct: ProtocolStructure): TreeNode[] => {
    const nodes: TreeNode[] = [];

    if (struct.variables && struct.variables.length > 0) {
      nodes.push({
        id: 'variables',
        name: 'Variables',
        type: 'variables',
        count: struct.variables.length,
        children: struct.variables.map((v: any) => ({
          id: v.var_id || v.id || v.OID,
          name: v.name || v.var_id || v.OID || 'Unknown',
          type: 'variable',
          data: v,
        })),
      });
    }

    if (struct.code_lists && struct.code_lists.length > 0) {
      nodes.push({
        id: 'code_lists',
        name: 'Code Lists',
        type: 'code_lists',
        count: struct.code_lists.length,
        children: struct.code_lists.map((c: any) => ({
          id: c.code_list_id || c.id || c.OID,
          name: c.name || c.code_list_id || 'Unknown',
          type: 'code_list',
          data: c,
        })),
      });
    }

    if (struct.events && struct.events.length > 0) {
      nodes.push({
        id: 'events',
        name: 'Events',
        type: 'events',
        count: struct.events.length,
        children: struct.events.map((e: any) => ({
          id: e.event_id || e.id || e.OID,
          name: e.name || e.event_id || 'Unknown',
          type: 'event',
          data: e,
        })),
      });
    }

    if (struct.conditions && struct.conditions.length > 0) {
      nodes.push({
        id: 'conditions',
        name: 'Conditions',
        type: 'conditions',
        count: struct.conditions.length,
        children: struct.conditions.map((c: any) => ({
          id: c.condition_id || c.id || c.OID,
          name: c.name || c.condition_id || 'Unknown',
          type: 'condition',
          data: c,
        })),
      });
    }

    if (struct.evaluations && struct.evaluations.length > 0) {
      nodes.push({
        id: 'evaluations',
        name: 'Evaluations',
        type: 'evaluations',
        count: struct.evaluations.length,
        children: struct.evaluations.map((e: any) => ({
          id: e.evaluation_id || e.id || e.OID,
          name: e.name || e.evaluation_id || 'Unknown',
          type: 'evaluation',
          data: e,
        })),
      });
    }

    if (struct.actions && struct.actions.length > 0) {
      nodes.push({
        id: 'actions',
        name: 'Actions',
        type: 'actions',
        count: struct.actions.length,
        children: struct.actions.map((a: any) => ({
          id: a.action_id || a.id || a.OID,
          name: a.name || a.action_id || 'Unknown',
          type: 'action',
          data: a,
        })),
      });
    }

    if (struct.forms && struct.forms.length > 0) {
      nodes.push({
        id: 'forms',
        name: 'Forms',
        type: 'forms',
        count: struct.forms.length,
        children: struct.forms.map((f: any) => ({
          id: f.form_id || f.id,
          name: f.name || f.form_id || 'Unknown Form',
          type: 'form',
          formId: f.form_id || f.id,
          data: f,
        })),
      });
    }

    return nodes;
  };

  // ============================================
  // NODE INTERACTION
  // ============================================

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleNodeSelect = (node: TreeNode) => {
    setSelectedNode(node);
    setSelectedElement(null);
    setElementDetail(null);

    // If it's a category node, show its children as a list
    if (node.children && node.children.length > 0) {
      setElementList(node.children);
    } else {
      setElementList([]);
      // If it's a leaf node, load element detail
      handleElementSelect(node);
    }
  };

  const handleElementSelect = useCallback(async (element: TreeNode | SelectedElement) => {
    if (!trialId || !sessionId || !platform) return;

    const sel: SelectedElement = {
      id: element.id,
      type: element.type,
      name: ('name' in element) ? element.name : undefined,
      formId: ('formId' in element) ? element.formId : undefined,
      data: ('data' in element) ? element.data : undefined,
    };
    setSelectedElement(sel);

    // For leaf elements with embedded data, show it directly
    if (sel.data) {
      setElementDetail(sel.data);
      return;
    }

    // Otherwise load from extractor API
    setElementDetailLoading(true);
    try {
      let detail = null;
      switch (sel.type) {
        case 'variable':
          detail = await extractorService.getVariable(trialId, sessionId, platform as 'app' | 'web', sel.id);
          break;
        case 'condition':
          detail = await extractorService.getCondition(trialId, sessionId, platform as 'app' | 'web', sel.id);
          break;
        case 'evaluation':
          detail = await extractorService.getEvaluation(trialId, sessionId, platform as 'app' | 'web', sel.id);
          break;
        case 'action':
          detail = await extractorService.getAction(trialId, sessionId, platform as 'app' | 'web', sel.id);
          break;
        case 'form':
          detail = await extractorService.getFormContent(trialId, sessionId, platform as 'app' | 'web', sel.id);
          break;
        default:
          detail = sel.data;
      }
      setElementDetail(detail);
    } catch (err) {
      console.error('[Workspace] Failed to load element detail:', err);
      setElementDetail(null);
    } finally {
      setElementDetailLoading(false);
    }
  }, [trialId, sessionId, platform]);

  // ============================================
  // ICON HELPERS
  // ============================================

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'variables':
      case 'variable':
        return <VariableIcon fontSize="small" />;
      case 'code_lists':
      case 'code_list':
        return <CodeListIcon fontSize="small" />;
      case 'events':
      case 'event':
        return <EventIcon fontSize="small" />;
      case 'conditions':
      case 'condition':
        return <ConditionIcon fontSize="small" />;
      case 'evaluations':
      case 'evaluation':
        return <EvaluationIcon fontSize="small" />;
      case 'actions':
      case 'action':
        return <ActionIcon fontSize="small" />;
      case 'forms':
        return <FolderIcon fontSize="small" />;
      case 'form':
        return <FormIcon fontSize="small" />;
      case 'settings':
        return <SettingsIcon fontSize="small" />;
      default:
        return <ContentIcon fontSize="small" />;
    }
  };

  const getRequirementStatusIcon = (status: string) => {
    switch (status) {
      case 'committed':
        return <DoneIcon fontSize="small" color="success" />;
      case 'planned':
      case 'active':
        return <PlannedIcon fontSize="small" color="primary" />;
      default:
        return <PendingIcon fontSize="small" color="disabled" />;
    }
  };

  // ============================================
  // RENDER: LOADING / ERROR
  // ============================================

  if (loading) {
    return (
      <ProgressOverlay inProgress={true} message="Loading workspace..." fullOverlay={true} />
    );
  }

  if (error) {
    return (
      <Box sx={{p: 4}}>
        <Alert severity="error" sx={{mb: 2}}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/')}>
          Back to Protocols
        </Button>
      </Box>
    );
  }

  // ============================================
  // RENDER: STRUCTURE TREE (LEFT PANEL)
  // ============================================

  const renderTreeNode = (node: TreeNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode?.id === node.id;
    const hasChildren = node.children && node.children.length > 0;
    const isCategory = ['variables', 'code_lists', 'events', 'conditions', 'evaluations', 'actions', 'forms'].includes(node.type);

    return (
      <React.Fragment key={node.id}>
        <ListItemButton
          selected={isSelected}
          onClick={() => {
            if (isCategory && hasChildren) {
              toggleNode(node.id);
            }
            handleNodeSelect(node);
          }}
          sx={{
            pl: 2 + depth * 2,
            py: 0.5,
            minHeight: 36,
            '&.Mui-selected': {
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              '&:hover': {bgcolor: 'primary.main'},
            },
          }}
        >
          <ListItemIcon sx={{minWidth: 32}}>
            {getNodeIcon(node.type)}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography variant="body2" noWrap sx={{fontSize: '0.85rem'}}>
                  {node.name}
                </Typography>
                {node.count !== undefined && (
                  <Chip label={node.count} size="small" sx={{height: 18, fontSize: '0.7rem'}} />
                )}
              </Box>
            }
          />
          {isCategory && hasChildren && (
            <IconButton size="small" sx={{p: 0}}>
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </ListItemButton>

        {isCategory && hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.children!.map((child) => renderTreeNode(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const renderStructureTree = () => (
    <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
      <Box sx={{p: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
        <Typography variant="subtitle2" sx={{fontWeight: 600}}>
          Protocol Structure
        </Typography>
      </Box>
      <Box sx={{flex: 1, overflow: 'auto'}}>
        {treeNodes.length === 0 ? (
          <Box sx={{p: 2, textAlign: 'center'}}>
            <Typography variant="body2" color="text.secondary">
              No protocol structure loaded
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {treeNodes.map((node) => renderTreeNode(node))}
          </List>
        )}
      </Box>
    </Box>
  );

  // ============================================
  // RENDER: CONTENT VIEWER (CENTER PANEL)
  // ============================================

  const renderContentViewer = () => {
    if (!selectedNode && !selectedElement) {
      return (
        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
          <Typography color="text.secondary">
            Select an element from the structure tree to view its details
          </Typography>
        </Box>
      );
    }

    // Show element list for category nodes
    if (selectedNode && elementList.length > 0 && !selectedElement) {
      return (
        <Box sx={{height: '100%', overflow: 'auto'}}>
          <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
            <Typography variant="h6">
              {selectedNode.name}
              {selectedNode.count !== undefined && (
                <Chip label={`${selectedNode.count} items`} size="small" sx={{ml: 1}} />
              )}
            </Typography>
          </Box>
          <List dense>
            {elementList.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => handleElementSelect(item)}
                sx={{borderBottom: '1px solid', borderColor: 'divider'}}
              >
                <ListItemIcon sx={{minWidth: 32}}>
                  {getNodeIcon(item.type)}
                </ListItemIcon>
                <ListItemText
                  primary={item.name || item.id}
                  secondary={item.id !== item.name ? item.id : undefined}
                  primaryTypographyProps={{variant: 'body2'}}
                  secondaryTypographyProps={{variant: 'caption'}}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      );
    }

    // Show inline loading via ProgressIndicator
    if (elementDetailLoading) {
      return (
        <Box sx={{position: 'relative', height: '100%'}}>
          <ProgressIndicator inProgress={true} message="Loading element..." />
        </Box>
      );
    }

    if (elementDetail || selectedElement?.data) {
      const detail = elementDetail || selectedElement?.data;
      return (
        <Box sx={{height: '100%', overflow: 'auto'}}>
          <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
            <Typography variant="h6">
              {selectedElement?.name || selectedElement?.id || 'Element Detail'}
            </Typography>
            {selectedElement?.id && (
              <Typography variant="caption" color="text.secondary">
                ID: {selectedElement.id} | Type: {selectedElement.type}
              </Typography>
            )}
          </Box>
          <Box sx={{p: 2}}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                overflow: 'auto',
                maxHeight: 'calc(100vh - 280px)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)}
            </Paper>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
        <Typography color="text.secondary">No details available</Typography>
      </Box>
    );
  };

  // ============================================
  // RENDER: REQUIREMENTS PANEL (RIGHT PANEL)
  // ============================================

  const renderRequirementsPanel = () => {
    const reqList = requirements?.requirements || [];

    return (
      <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
        <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
          <Typography variant="subtitle1" sx={{fontWeight: 600}}>
            Requirements
          </Typography>
          {reqList.length > 0 && (
            <Chip label={`${reqList.length} items`} size="small" sx={{mt: 0.5}} />
          )}
        </Box>

        <Box sx={{flex: 1, overflow: 'auto'}}>
          {reqList.length === 0 ? (
            <Box sx={{p: 2, textAlign: 'center'}}>
              <Typography variant="body2" color="text.secondary">
                No requirements found for this session.
              </Typography>
            </Box>
          ) : (
            <List dense>
              {reqList.map((req: any, index: number) => (
                <React.Fragment key={req.id || index}>
                  <ListItemButton
                    sx={{
                      py: 1,
                      alignItems: 'flex-start',
                    }}
                  >
                    <ListItemIcon sx={{minWidth: 32, mt: 0.5}}>
                      {getRequirementStatusIcon(req.status || 'pending')}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                          <Chip
                            label={req.id || `REQ-${index + 1}`}
                            size="small"
                            variant="outlined"
                            sx={{height: 20, fontSize: '0.7rem'}}
                          />
                          {req.status && (
                            <Chip
                              label={req.status}
                              size="small"
                              color={
                                req.status === 'committed' ? 'success' :
                                req.status === 'active' ? 'primary' :
                                req.status === 'planned' ? 'info' : 'default'
                              }
                              sx={{height: 20, fontSize: '0.7rem'}}
                            />
                          )}
                        </Box>
                      }
                      secondary={req.content || req.description || req.text || ''}
                      secondaryTypographyProps={{
                        variant: 'body2',
                        sx: {mt: 0.5, lineHeight: 1.4},
                      }}
                    />
                  </ListItemButton>
                  {index < reqList.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Box>
    );
  };

  // ============================================
  // MAIN RENDER (using modular-ui PageLayout + MainInfoBar + DualResizableContainer)
  // ============================================

  return (
    <PageLayout.Root sx={{padding: 0, gap: 0, height: '100vh'}}>
      {/* Top Bar */}
      <PageLayout.Header showDivider={true} sx={{py: 0.5, px: 1}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, width: '100%'}}>
          <Tooltip title="Back to Protocols">
            <IconButton size="small" onClick={() => navigate('/')}>
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle1" sx={{fontWeight: 600}}>
            {trialId}
          </Typography>
          <Chip label={sessionName || sessionId} size="small" variant="outlined" />
          <Chip label={platform?.toUpperCase()} size="small" color="primary" />
          <Typography variant="body2" color="text.secondary" sx={{ml: 'auto'}}>
            Protocol Workspace
          </Typography>
        </Box>
      </PageLayout.Header>

      {/* Main Content Area: 3-column layout */}
      <PageLayout.Content sx={{height: '100%'}}>
        <Box sx={{display: 'flex', height: '100%', overflow: 'hidden'}}>
          {/* Left Panel: Structure Tree (fixed width) */}
          <Paper
            elevation={0}
            sx={{
              width: 280,
              minWidth: 280,
              borderRight: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {renderStructureTree()}
          </Paper>

          {/* Center + Right: DualResizableContainer */}
          <Box sx={{flex: 1, overflow: 'hidden'}}>
            <DualResizableContainer
              leftSection={{
                id: 'content-viewer',
                component: renderContentViewer(),
                minWidthPx: 300,
                initialPercentage: 65,
              }}
              rightSection={{
                id: 'requirements-panel',
                component: renderRequirementsPanel(),
                minWidthPx: 250,
                initialPercentage: 35,
              }}
            />
          </Box>
        </Box>
      </PageLayout.Content>
    </PageLayout.Root>
  );
};

export default ProtocolWorkspacePage;
